import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CalendarClock, Plus, Repeat, Target, Trash2 } from 'lucide-react';
import { PageHeader, TableSkeleton } from '@/components/shared/page';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox, Select } from '@/components/ui/misc';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormError } from '@/features/auth/auth-layout';
import { ApiRequestError, apiGet } from '@/lib/api';
import { formatDate, formatMoney } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  useBudgets,
  useDeleteBudget,
  useDeleteRecurringExpense,
  useRecurringExpenses,
  useSaveBudget,
  useSaveRecurringExpense,
  useVariance,
  type Budget,
  type RecurringExpense,
} from './api';

const CYCLES = [
  { value: 'WEEKLY', label: 'Every week' },
  { value: 'MONTHLY', label: 'Every month' },
  { value: 'QUARTERLY', label: 'Every quarter' },
  { value: 'HALF_YEARLY', label: 'Twice a year' },
  { value: 'ANNUAL', label: 'Once a year' },
];

/** The lines of business a revenue budget can be set against. */
const REVENUE_LINES = [
  { value: 'SAAS', label: 'SaaS subscriptions' },
  { value: 'SERVICE', label: 'Services' },
  { value: 'AMC', label: 'AMC and support' },
  { value: 'ONE_TIME', label: 'One-off work' },
  { value: 'ADDON', label: 'Add-ons' },
  { value: 'HARDWARE', label: 'Hardware' },
];

function currentPeriod(): string {
  const now = new Date();
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
}

function useExpenseCategories() {
  return useQuery({
    queryKey: ['expense-categories'],
    queryFn: () => apiGet<{ id: string; name: string }[]>('/finance/expense-categories'),
  });
}

function useVendors() {
  return useQuery({
    queryKey: ['vendors'],
    queryFn: () => apiGet<{ id: string; name: string }[]>('/finance/vendors'),
  });
}

/**
 * Budgets against actual, and the recurring costs the forecast leans on
 * (spec FCT-08, EXP-03).
 *
 * The two live on one page because they answer the same question from
 * different ends: what was planned, and what is definitely coming.
 */
export function BudgetsPage() {
  const [period, setPeriod] = useState(currentPeriod());
  const [editingBudget, setEditingBudget] = useState<Budget | 'new' | null>(null);
  const [editingCost, setEditingCost] = useState<RecurringExpense | 'new' | null>(null);

  const { data: budgets, isLoading } = useBudgets();
  const { data: variance } = useVariance(period);
  const { data: recurring } = useRecurringExpenses();

  const deleteBudget = useDeleteBudget();
  const deleteCost = useDeleteRecurringExpense();

  const monthlyTotal = (recurring ?? [])
    .filter((row) => row.isActive)
    .reduce((total, row) => {
      const amount = Number(row.amount ?? 0);
      const perMonth =
        row.cycle === 'WEEKLY'
          ? amount * 4.33
          : row.cycle === 'QUARTERLY'
            ? amount / 3
            : row.cycle === 'HALF_YEARLY'
              ? amount / 6
              : row.cycle === 'ANNUAL'
                ? amount / 12
                : amount;
      return total + perMonth;
    }, 0);

  return (
    <div>
      <PageHeader
        title="Budgets and standing costs"
        description="What was planned against what happened, and the costs that arrive whether anyone books them or not."
        actions={
          <Button onClick={() => setEditingBudget('new')}>
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            New budget
          </Button>
        }
      />

      <Card className="mb-4">
        <CardHeader className="flex-row items-start justify-between space-y-0">
          <div>
            <CardTitle className="text-base">Budget against actual</CardTitle>
            <CardDescription>
              An expense under budget is favourable; revenue over budget is favourable. The page says
              which, so nobody has to guess in a board meeting.
            </CardDescription>
          </div>
          <Input
            value={period}
            onChange={(event) => setPeriod(event.target.value)}
            placeholder="2026-09"
            className="w-32"
          />
        </CardHeader>
        <CardContent>
          {!variance || variance.rows.length === 0 ? (
            <div className="rounded-lg border border-dashed py-10 text-center">
              <Target className="mx-auto mb-2 h-5 w-5 text-muted-foreground" />
              <p className="text-sm font-medium">No budget set for {period}</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Set one and this table fills in from the ledger.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Line</TableHead>
                  <TableHead className="text-right">Budgeted</TableHead>
                  <TableHead className="text-right">Actual</TableHead>
                  <TableHead className="text-right">Variance</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {variance.rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <span className="text-sm font-medium">{row.label}</span>
                      <Badge variant="outline" className="ml-2 text-[10px]">
                        {row.kind === 'EXPENSE' ? 'spend' : 'revenue'}
                      </Badge>
                    </TableCell>
                    <TableCell className="tabular text-right">{formatMoney(row.budgeted)}</TableCell>
                    <TableCell className="tabular text-right">{formatMoney(row.actual)}</TableCell>
                    <TableCell className="text-right">
                      <span
                        className={cn(
                          'tabular font-medium',
                          row.favourable ? 'text-emerald-700' : 'text-red-700',
                        )}
                      >
                        {formatMoney(row.variance)}
                        {row.variancePercent !== null && (
                          <span className="ml-1.5 text-xs font-normal">
                            ({row.variancePercent > 0 ? '+' : ''}
                            {row.variancePercent}%)
                          </span>
                        )}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span
                        className={cn(
                          'text-xs',
                          row.favourable ? 'text-emerald-700' : 'text-muted-foreground',
                        )}
                      >
                        {row.favourable ? 'On track' : 'Over'}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Every budget</CardTitle>
            <CardDescription>Set by year, quarter or month.</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <TableSkeleton rows={4} columns={3} />
            ) : (budgets ?? []).length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">Nothing budgeted yet.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Period</TableHead>
                    <TableHead>Line</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(budgets ?? []).map((budget) => (
                    <TableRow
                      key={budget.id}
                      className="cursor-pointer"
                      onClick={() => setEditingBudget(budget)}
                    >
                      <TableCell className="text-sm">{budget.periodKey}</TableCell>
                      <TableCell className="text-sm">
                        {budget.category?.name ?? budget.revenueLine ?? '—'}
                      </TableCell>
                      <TableCell className="tabular text-right">{formatMoney(budget.amount)}</TableCell>
                      <TableCell className="text-right">
                        <button
                          onClick={(event) => {
                            event.stopPropagation();
                            deleteBudget.mutate(budget.id);
                          }}
                          className="text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex-row items-start justify-between space-y-0">
            <div>
              <CardTitle className="text-base">Standing costs</CardTitle>
              <CardDescription>
                Rent, hosting, licences — roughly {formatMoney(monthlyTotal.toFixed(2))} a month.
              </CardDescription>
            </div>
            <Button size="sm" variant="outline" onClick={() => setEditingCost('new')}>
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              Add
            </Button>
          </CardHeader>
          <CardContent>
            {(recurring ?? []).length === 0 ? (
              <div className="rounded-lg border border-dashed py-10 text-center">
                <Repeat className="mx-auto mb-2 h-5 w-5 text-muted-foreground" />
                <p className="text-sm font-medium">No standing costs recorded</p>
                <p className="mt-1 max-w-xs text-sm text-muted-foreground">
                  Without these the forecast shows only the money coming in, which is the most
                  dangerous kind of wrong.
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cost</TableHead>
                    <TableHead>Cycle</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(recurring ?? []).map((row) => (
                    <TableRow
                      key={row.id}
                      className={cn('cursor-pointer', !row.isActive && 'opacity-50')}
                      onClick={() => setEditingCost(row)}
                    >
                      <TableCell>
                        <span className="text-sm font-medium">{row.name}</span>
                        {row.autoCreateExpense && (
                          <Badge variant="secondary" className="ml-2 text-[10px]">
                            books itself
                          </Badge>
                        )}
                        {row.category && (
                          <p className="text-xs text-muted-foreground">{row.category.name}</p>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {CYCLES.find((cycle) => cycle.value === row.cycle)?.label ?? row.cycle}
                        <span className="block text-xs">day {row.dueDay}</span>
                      </TableCell>
                      <TableCell className="tabular text-right">{formatMoney(row.amount)}</TableCell>
                      <TableCell className="text-right">
                        <button
                          onClick={(event) => {
                            event.stopPropagation();
                            deleteCost.mutate(row.id);
                          }}
                          className="text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {editingBudget && (
        <BudgetDialog
          budget={editingBudget === 'new' ? null : editingBudget}
          defaultPeriod={period}
          onClose={() => setEditingBudget(null)}
        />
      )}
      {editingCost && (
        <RecurringDialog
          cost={editingCost === 'new' ? null : editingCost}
          onClose={() => setEditingCost(null)}
        />
      )}
    </div>
  );
}

function BudgetDialog({
  budget,
  defaultPeriod,
  onClose,
}: {
  budget: Budget | null;
  defaultPeriod: string;
  onClose: () => void;
}) {
  const { data: categories } = useExpenseCategories();
  const save = useSaveBudget();

  const [periodKey, setPeriodKey] = useState(budget?.periodKey ?? defaultPeriod);
  const [kind, setKind] = useState<'EXPENSE' | 'REVENUE'>(budget?.kind ?? 'EXPENSE');
  const [categoryId, setCategoryId] = useState(budget?.category?.id ?? '');
  const [revenueLine, setRevenueLine] = useState(budget?.revenueLine ?? '');
  const [amount, setAmount] = useState(budget?.amount ?? '');
  const [note, setNote] = useState(budget?.note ?? '');

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{budget ? 'Edit budget' : 'New budget'}</DialogTitle>
          <DialogDescription>
            A period of 2026 covers the year, 2026-Q3 a quarter, 2026-09 a month.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="budget-period">Period</Label>
              <Input
                id="budget-period"
                value={periodKey}
                onChange={(event) => setPeriodKey(event.target.value)}
                placeholder="2026-09"
              />
            </div>
            <div>
              <Label htmlFor="budget-kind">Kind</Label>
              <Select
                id="budget-kind"
                value={kind}
                onChange={(event) => setKind(event.target.value as 'EXPENSE' | 'REVENUE')}
              >
                <option value="EXPENSE">Spending</option>
                <option value="REVENUE">Revenue</option>
              </Select>
            </div>
          </div>

          {kind === 'EXPENSE' ? (
            <div>
              <Label htmlFor="budget-category">Category</Label>
              <Select
                id="budget-category"
                value={categoryId}
                onChange={(event) => setCategoryId(event.target.value)}
              >
                <option value="">Choose a category</option>
                {(categories ?? []).map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </Select>
            </div>
          ) : (
            <div>
              <Label htmlFor="budget-line">Line of business</Label>
              <Select
                id="budget-line"
                value={revenueLine}
                onChange={(event) => setRevenueLine(event.target.value)}
              >
                <option value="">Choose a line</option>
                {REVENUE_LINES.map((line) => (
                  <option key={line.value} value={line.value}>
                    {line.label}
                  </option>
                ))}
              </Select>
            </div>
          )}

          <div>
            <Label htmlFor="budget-amount">Amount</Label>
            <Input
              id="budget-amount"
              value={amount ?? ''}
              onChange={(event) => setAmount(event.target.value)}
              placeholder="50000.00"
            />
          </div>

          <div>
            <Label htmlFor="budget-note">Note</Label>
            <Input
              id="budget-note"
              value={note ?? ''}
              onChange={(event) => setNote(event.target.value)}
              placeholder="Agreed at the September planning meeting"
            />
          </div>

          {save.isError && <FormError message={(save.error as ApiRequestError).message} />}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={save.isPending || !amount}
            onClick={() =>
              save.mutate(
                {
                  id: budget?.id,
                  periodKey,
                  kind,
                  categoryId: kind === 'EXPENSE' ? categoryId || null : null,
                  revenueLine: kind === 'REVENUE' ? revenueLine || null : null,
                  amount: String(amount),
                  note: note || undefined,
                },
                { onSuccess: onClose },
              )
            }
          >
            Save budget
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RecurringDialog({ cost, onClose }: { cost: RecurringExpense | null; onClose: () => void }) {
  const { data: categories } = useExpenseCategories();
  const { data: vendors } = useVendors();
  const save = useSaveRecurringExpense();

  const [name, setName] = useState(cost?.name ?? '');
  const [amount, setAmount] = useState(cost?.amount ?? '');
  const [cycle, setCycle] = useState(cost?.cycle ?? 'MONTHLY');
  const [dueDay, setDueDay] = useState(cost?.dueDay ?? 1);
  const [startDate, setStartDate] = useState(
    cost?.startDate?.slice(0, 10) ?? new Date().toISOString().slice(0, 10),
  );
  const [endDate, setEndDate] = useState(cost?.endDate?.slice(0, 10) ?? '');
  const [categoryId, setCategoryId] = useState(cost?.category?.id ?? '');
  const [vendorId, setVendorId] = useState(cost?.vendor?.id ?? '');
  const [isActive, setIsActive] = useState(cost?.isActive ?? true);
  const [autoCreate, setAutoCreate] = useState(cost?.autoCreateExpense ?? false);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{cost ? 'Edit standing cost' : 'New standing cost'}</DialogTitle>
          <DialogDescription>
            A cost that arrives on a rhythm. It feeds the forecast; ticking &ldquo;book it
            automatically&rdquo; also creates the expense record each cycle.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label htmlFor="cost-name">What it is</Label>
            <Input
              id="cost-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Office rent"
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label htmlFor="cost-amount">Amount</Label>
              <Input
                id="cost-amount"
                value={amount ?? ''}
                onChange={(event) => setAmount(event.target.value)}
                placeholder="12000.00"
              />
            </div>
            <div>
              <Label htmlFor="cost-cycle">Cycle</Label>
              <Select id="cost-cycle" value={cycle} onChange={(event) => setCycle(event.target.value)}>
                {CYCLES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="cost-day">Day</Label>
              <Input
                id="cost-day"
                type="number"
                min={1}
                max={28}
                value={dueDay}
                onChange={(event) => setDueDay(Number(event.target.value))}
              />
              <p className="mt-1 text-xs text-muted-foreground">1–28</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="cost-start">Starts</Label>
              <Input
                id="cost-start"
                type="date"
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="cost-end">Ends (optional)</Label>
              <Input
                id="cost-end"
                type="date"
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="cost-category">Category</Label>
              <Select
                id="cost-category"
                value={categoryId}
                onChange={(event) => setCategoryId(event.target.value)}
              >
                <option value="">Uncategorised</option>
                {(categories ?? []).map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="cost-vendor">Vendor</Label>
              <Select id="cost-vendor" value={vendorId} onChange={(event) => setVendorId(event.target.value)}>
                <option value="">None</option>
                {(vendors ?? []).map((vendor) => (
                  <option key={vendor.id} value={vendor.id}>
                    {vendor.name}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={autoCreate} onCheckedChange={(value) => setAutoCreate(Boolean(value))} />
            Book it automatically as an expense each cycle
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={isActive} onCheckedChange={(value) => setIsActive(Boolean(value))} />
            Active
          </label>

          {cost?.lastGeneratedFor && (
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <CalendarClock className="h-3 w-3" />
              Last booked for {formatDate(cost.lastGeneratedFor)}
            </p>
          )}

          {save.isError && <FormError message={(save.error as ApiRequestError).message} />}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={save.isPending || !name.trim() || !amount}
            onClick={() =>
              save.mutate(
                {
                  id: cost?.id,
                  name,
                  amount: String(amount),
                  cycle,
                  dueDay,
                  startDate,
                  endDate: endDate || null,
                  categoryId: categoryId || null,
                  vendorId: vendorId || null,
                  isActive,
                  autoCreateExpense: autoCreate,
                },
                { onSuccess: onClose },
              )
            }
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
